import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/content';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9q9c4ue3oq4jqumtifg';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormState('success');
      setFormMessage('提交成功，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormState('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('website_alt');

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('提交成功！感谢你的留言，我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormState('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormState('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <main className="pt-20" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mb-8">
        <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
          <Link to="/" className="hover:underline cursor-pointer" style={{ color: 'oklch(var(--foreground-500))' }}>首页</Link>
          <span style={{ color: 'oklch(var(--foreground-400))' }}>/</span>
          <span className="font-medium" style={{ color: 'oklch(var(--foreground-950))' }}>关于我们</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="w-full py-16 md:py-20" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 md:w-18 md:h-18 flex items-center justify-center mx-auto mb-5 rounded-full" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
            <i className="ri-seedling-line text-3xl" style={{ color: 'oklch(var(--primary-500))' }}></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-heading mb-4" style={{ color: 'oklch(var(--foreground-950))' }}>
            关于雏鸟
          </h1>
          <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: 'oklch(var(--foreground-700))' }}>
            雏鸟app诞生于2023年，由一群热爱社交与科技的年轻人创立。我们相信，每个人都是一只等待展翅的雏鸟，而好的社交关系就是助你飞翔的那阵风。
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="w-full py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading mb-5" style={{ color: 'oklch(var(--foreground-950))' }}>
                我们的故事
              </h2>
              <div className="space-y-4">
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'oklch(var(--foreground-700))' }}>
                  创始人团队曾经也是一群在陌生城市打拼的年轻人。我们深刻体会到，在快节奏的都市生活中，找到真正志同道合的朋友有多难。传统的社交平台充斥着大量的无效信息和浅层互动，很难帮助你建立有深度的连接。
                </p>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'oklch(var(--foreground-700))' }}>
                  于是，雏鸟app应运而生。我们希望通过智能算法和温暖的社区设计，让每一个年轻人都能在这里找到属于自己的社群，获得成长的力量。从最初的小团队到如今服务超过500万用户，雏鸟app始终坚守初心——让社交回归真诚，让成长有人陪伴。
                </p>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'oklch(var(--foreground-700))' }}>
                  2025年，雏鸟app获得了「最佳社交创新应用」奖项，这是对我们最大的肯定。未来，我们将继续优化产品体验，推出更多有趣的功能，陪伴每一位用户成长。
                </p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden h-72 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20modern%20cozy%20workspace%20with%20young%20creative%20team%20collaborating%20around%20a%20table%2C%20plants%20and%20natural%20light%2C%20coral%20and%20teal%20accent%20colors%2C%20friendly%20atmosphere%2C%20minimal%20design%2C%20editorial%20style%20photography&width=800&height=600&seq=about-brand-story&orientation=landscape"
                alt="雏鸟app团队"
                className="w-full h-full object-cover object-top"
                width={800}
                height={600}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="w-full py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading mb-10 text-center" style={{ color: 'oklch(var(--foreground-950))' }}>
            我们的价值观
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-heart-line',
                title: '真诚连接',
                desc: '我们鼓励真实的自我表达，让每一次相遇都建立在真诚的基础上。',
              },
              {
                icon: 'ri-shield-star-line',
                title: '安全第一',
                desc: '用户的安全和隐私是我们最优先的考量，从不妥协。',
              },
              {
                icon: 'ri-lightbulb-line',
                title: '持续创新',
                desc: '不断探索新的技术和方法，让社交体验变得更加美好。',
              },
            ].map((v, i) => (
              <div
                key={i}
                className="p-6 rounded-lg text-center"
                style={{ backgroundColor: 'oklch(var(--background-50))' }}
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                  <i className={`${v.icon} text-xl`} style={{ color: 'oklch(var(--accent-500))' }}></i>
                </div>
                <h3 className="text-base font-bold mb-2" style={{ color: 'oklch(var(--foreground-950))' }}>{v.title}</h3>
                <p className="text-sm" style={{ color: 'oklch(var(--foreground-600))' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="w-full py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading mb-10 text-center" style={{ color: 'oklch(var(--foreground-950))' }}>
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-5 rounded-lg"
                style={{ backgroundColor: 'oklch(var(--background-100))' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'oklch(var(--foreground-950))' }}>{t.name}</div>
                    <div className="text-xs" style={{ color: 'oklch(var(--foreground-500))' }}>{t.role}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-xs" style={{ color: 'oklch(var(--secondary-500))' }}></i>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'oklch(var(--foreground-700))' }}>
                  {t.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="w-full py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading mb-3" style={{ color: 'oklch(var(--foreground-950))' }}>
              联系我们
            </h2>
            <p className="text-sm" style={{ color: 'oklch(var(--foreground-600))' }}>
              有任何问题或合作意向，欢迎给我们留言
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="p-6 rounded-lg"
                style={{ backgroundColor: 'oklch(var(--background-50))' }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'oklch(var(--foreground-700))' }}>
                      姓名 <span style={{ color: 'oklch(var(--primary-500))' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md text-sm outline-none transition-colors duration-200"
                      style={{
                        backgroundColor: 'oklch(var(--background-100))',
                        color: 'oklch(var(--foreground-950))',
                        border: '1px solid oklch(var(--background-200))',
                      }}
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'oklch(var(--foreground-700))' }}>
                      邮箱 <span style={{ color: 'oklch(var(--primary-500))' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md text-sm outline-none transition-colors duration-200"
                      style={{
                        backgroundColor: 'oklch(var(--background-100))',
                        color: 'oklch(var(--foreground-950))',
                        border: '1px solid oklch(var(--background-200))',
                      }}
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'oklch(var(--foreground-700))' }}>
                    留言内容 <span style={{ color: 'oklch(var(--primary-500))' }}>*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-md text-sm outline-none resize-none transition-colors duration-200"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      color: 'oklch(var(--foreground-950))',
                      border: '1px solid oklch(var(--background-200))',
                    }}
                    placeholder="请输入你的留言内容（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200"
                  style={{
                    backgroundColor: formState === 'submitting' ? 'oklch(var(--primary-300))' : 'oklch(var(--primary-500))',
                    color: '#fff',
                  }}
                  onMouseEnter={(e) => {
                    if (formState !== 'submitting') {
                      e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formState !== 'submitting') {
                      e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))';
                    }
                  }}
                >
                  {formState === 'submitting' ? '提交中...' : '发送留言'}
                </button>
                {formMessage && (
                  <p
                    className={`mt-3 text-sm text-center ${formState === 'success' ? '' : ''}`}
                    style={{
                      color: formState === 'success'
                        ? 'oklch(var(--accent-600))'
                        : 'oklch(var(--primary-600))',
                    }}
                  >
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
            <div className="flex flex-col gap-5">
              <div className="p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-full mb-3" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                  <i className="ri-mail-line" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'oklch(var(--foreground-950))' }}>邮箱</h3>
                <p className="text-xs" style={{ color: 'oklch(var(--foreground-600))' }}>support@chuniao.app</p>
              </div>
              <div className="p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-full mb-3" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                  <i className="ri-time-line" style={{ color: 'oklch(var(--accent-500))' }}></i>
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'oklch(var(--foreground-950))' }}>工作时间</h3>
                <p className="text-xs" style={{ color: 'oklch(var(--foreground-600))' }}>周一至周日 9:00-21:00</p>
              </div>
              <div className="p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-full mb-3" style={{ backgroundColor: 'oklch(var(--secondary-100))' }}>
                  <i className="ri-map-pin-line" style={{ color: 'oklch(var(--secondary-500))' }}></i>
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'oklch(var(--foreground-950))' }}>公司地址</h3>
                <p className="text-xs" style={{ color: 'oklch(var(--foreground-600))' }}>广东省深圳市南山区科技园南区雏鸟大厦</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于雏鸟app',
            description: '雏鸟app是专为年轻用户打造的社交成长平台，提供兴趣匹配、内容分享与社区互动功能。',
            url: 'https://chuniao.app/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '雏鸟app官方介绍与下载指南',
              url: 'https://chuniao.app',
            },
          }),
        }}
      />
    </main>
  );
}